<script>
import appConfig from '@src/app.config'
import Layout from '@layouts/main'
import PageHeader from '@components/page-header'
import axios from 'axios'
import VueApexCharts from 'vue-apexcharts'
import jsPDF from 'jspdf'

export default {
  page: {
    title: 'Prisma Cloud Vulnerabilities',
    meta: [{ name: 'description', content: appConfig.description }]
  },
  components: {
    Layout,
    PageHeader,
    apexchart: VueApexCharts
  },
  data() {
    return {
      title: 'Prisma Cloud Vulnerabilities Dashboard',
      loading: true,
      updated: '',

      // Stats data
      stats: {
        total_count: 0,
        critical_count: 0,
        high_count: 0,
        medium_count: 0,
        low_count: 0,
        exploitable_count: 0,
        internet_exposed_count: 0,
        patchable_count: 0,
        cisa_kev_count: 0,
        unique_assets: 0,
        unique_cves: 0
      },

      // Vulnerability data
      vulnerabilities: [],
      filteredVulnerabilities: [],

      // Filter options
      selectedSeverities: ['critical', 'high'],
      severityOptions: [
        { value: 'critical', text: 'Critical' },
        { value: 'high', text: 'High' },
        { value: 'medium', text: 'Medium' },
        { value: 'low', text: 'Low' }
      ],

      selectedAssetType: 'all',
      assetTypeOptions: [
        { value: 'all', text: 'All Types' },
        { value: 'host', text: 'Host' },
        { value: 'package', text: 'Package' }
      ],

      selectedServiceProvider: 'all',
      serviceProviders: [],

      selectedPatchable: 'all',
      patchableOptions: [
        { value: 'all', text: 'All' },
        { value: 'Y', text: 'Patchable' },
        { value: 'N', text: 'Not Patchable' }
      ],

      selectedExploitable: 'all',
      exploitableOptions: [
        { value: 'all', text: 'All' },
        { value: 'Y', text: 'Exploitable' },
        { value: 'N', text: 'Not Exploitable' }
      ],

      selectedCisaKev: 'all',
      cisaKevOptions: [
        { value: 'all', text: 'All' },
        { value: 'Y', text: 'CISA KEV' },
        { value: 'N', text: 'Not CISA KEV' }
      ],

      selectedInternetExposed: 'all',
      internetExposedOptions: [
        { value: 'all', text: 'All' },
        { value: 'Y', text: 'Internet Exposed' },
        { value: 'N', text: 'Not Exposed' }
      ],

      selectedDateRange: '90',
      dateRangeOptions: [
        { value: '7', text: 'Last 7 Days' },
        { value: '30', text: 'Last 30 Days' },
        { value: '60', text: 'Last 60 Days' },
        { value: '90', text: 'Last 90 Days' },
        { value: '180', text: 'Last 180 Days' },
        { value: '365', text: 'Last Year' }
      ],

      searchQuery: '',

      // Chart data
      severityDistribution: [],
      topCves: [],
      topAssets: [],
      topPackages: [],
      attackVectors: [],
      assetTypes: [],
      trends: [],

      // Chart options
      severityChartOptions: {
        chart: {
          type: 'donut',
          height: 280
        },
        labels: [],
        colors: ['#802020', '#DC001E', '#FECA00', '#01AD00'],
        legend: {
          position: 'bottom',
          fontSize: '12px'
        },
        plotOptions: {
          pie: {
            donut: {
              size: '60%',
              labels: {
                show: true,
                total: {
                  show: true,
                  label: 'Total',
                  formatter: function (w) {
                    return w.globals.seriesTotals.reduce((a, b) => a + b, 0)
                  }
                }
              }
            }
          }
        }
      },
      severityChartSeries: [],

      topCvesChartOptions: {
        chart: {
          type: 'bar',
          height: 320,
          toolbar: { show: false }
        },
        plotOptions: {
          bar: {
            horizontal: true,
            distributed: true,
            barHeight: '70%'
          }
        },
        dataLabels: {
          enabled: true,
          offsetX: 30,
          style: { fontSize: '12px', colors: ['#fff'] }
        },
        xaxis: {
          categories: [],
          title: { text: 'Count' }
        },
        yaxis: {
          labels: {
            maxWidth: 200
          }
        },
        colors: [],
        legend: { show: false },
        tooltip: {
          y: {
            formatter: function(val) { return val + ' occurrences' }
          }
        }
      },
      topCvesChartSeries: [],

      topAssetsChartOptions: {
        chart: {
          type: 'bar',
          height: 320,
          toolbar: { show: false }
        },
        plotOptions: {
          bar: {
            horizontal: true,
            distributed: true,
            barHeight: '70%'
          }
        },
        dataLabels: {
          enabled: true,
          offsetX: 30,
          style: { fontSize: '12px', colors: ['#fff'] }
        },
        xaxis: {
          categories: [],
          title: { text: 'Vulnerability Count' }
        },
        yaxis: {
          labels: {
            maxWidth: 200
          }
        },
        colors: [],
        legend: { show: false }
      },
      topAssetsChartSeries: [],

      topPackagesChartOptions: {
        chart: {
          type: 'bar',
          height: 320,
          toolbar: { show: false }
        },
        plotOptions: {
          bar: {
            horizontal: true,
            distributed: true,
            barHeight: '70%'
          }
        },
        dataLabels: {
          enabled: true,
          offsetX: 30,
          style: { fontSize: '12px', colors: ['#fff'] }
        },
        xaxis: {
          categories: [],
          title: { text: 'Vulnerability Count' }
        },
        yaxis: {
          labels: {
            maxWidth: 200
          }
        },
        colors: '#556ee6',
        legend: { show: false }
      },
      topPackagesChartSeries: [],

      attackVectorChartOptions: {
        chart: {
          type: 'pie',
          height: 280
        },
        labels: [],
        colors: ['#5470C6', '#73C0DE', '#9A60B4', '#EA7CCC', '#6c757d'],
        legend: {
          position: 'bottom',
          fontSize: '12px'
        }
      },
      attackVectorChartSeries: [],

      assetTypeChartOptions: {
        chart: {
          type: 'donut',
          height: 280
        },
        labels: [],
        colors: ['#5470C6', '#73C0DE', '#9A60B4', '#EA7CCC'],
        legend: {
          position: 'bottom',
          fontSize: '11px',
          horizontalAlign: 'center',
          itemMargin: {
            horizontal: 8,
            vertical: 3
          }
        },
        plotOptions: {
          pie: {
            donut: {
              size: '55%'
            }
          }
        }
      },
      assetTypeChartSeries: [],

      trendsChartOptions: {
        chart: {
          type: 'area',
          height: 320,
          toolbar: { show: false }
        },
        dataLabels: { enabled: false },
        stroke: { curve: 'smooth', width: 2 },
        xaxis: {
          categories: [],
          title: { text: 'Month' }
        },
        yaxis: {
          title: { text: 'Vulnerabilities' }
        },
        colors: ['#556ee6', '#802020', '#DC001E'],
        fill: {
          type: 'gradient',
          gradient: {
            shadeIntensity: 1,
            opacityFrom: 0.7,
            opacityTo: 0.3
          }
        }
      },
      trendsChartSeries: [],

      // Table configuration
      columns: [
        { key: 'CVE_ID', label: 'CVE ID', sortable: true },
        { key: 'ASSET_NAME', label: 'Asset', sortable: true },
        { key: 'ASSET_TYPE', label: 'Type', sortable: true },
        { key: 'OWNER', label: 'Owner', sortable: true },
        { key: 'SEVERITY', label: 'Severity', sortable: true },
        { key: 'CVSS_BASE_SCORE', label: 'CVSS', sortable: true },
        { key: 'PACKAGE_NAME', label: 'Package', sortable: true },
        { key: 'PATCHABLE', label: 'Patch', sortable: true },
        { key: 'EXPLOITABLE', label: 'Exploit', sortable: true },
        { key: 'INTERNET_EXPOSED', label: 'Exposed', sortable: true },
        { key: 'ATTACK_VECTOR', label: 'Vector', sortable: true },
        { key: 'DISCOVERED_DATE', label: 'Discovered', sortable: true }
      ],

      sortKey: 'DISCOVERED_DATE',
      sortDirection: 'desc',

      // Pagination
      currentPage: 1,
      pageSize: 50,
      totalCount: 0,
      totalPages: 0,

      // Export
      exporting: false,

      // Table loading state
      tableLoading: false,

      items: [
        { text: 'Home', href: '/' },
        { text: 'Prisma Cloud', href: '/prismacloud' },
        { text: 'Vulnerabilities', active: true }
      ],

      appConfig: process.env.NODE_ENV === 'production' ? require('@src/app.config') : require('@src/app.config.dev')
    }
  },

  computed: {
    filterParams() {
      const params = {}

      if (this.selectedSeverities.length > 0 && this.selectedSeverities.length < 4) {
        params.severity = this.selectedSeverities.join(',')
      }

      if (this.selectedAssetType !== 'all') {
        params.asset_type = this.selectedAssetType
      }

      if (this.selectedServiceProvider !== 'all') {
        params.service_provider = this.selectedServiceProvider
      }

      if (this.selectedPatchable !== 'all') {
        params.patchable = this.selectedPatchable
      }

      if (this.selectedExploitable !== 'all') {
        params.exploitable = this.selectedExploitable
      }

      if (this.selectedCisaKev !== 'all') {
        params.cisa_kev = this.selectedCisaKev
      }

      if (this.selectedInternetExposed !== 'all') {
        params.internet_exposed = this.selectedInternetExposed
      }

      if (this.selectedDateRange) {
        params.date_range = this.selectedDateRange
      }

      if (this.searchQuery) {
        params.search = this.searchQuery
      }

      return params
    }
  },

  watch: {
    selectedSeverities: { handler() { this.loadAllData() } },
    selectedAssetType: { handler() { this.loadAllData() } },
    selectedServiceProvider: { handler() { this.loadAllData() } },
    selectedPatchable: { handler() { this.loadAllData() } },
    selectedExploitable: { handler() { this.loadAllData() } },
    selectedCisaKev: { handler() { this.loadAllData() } },
    selectedInternetExposed: { handler() { this.loadAllData() } },
    selectedDateRange: { handler() { this.loadAllData() } },
    searchQuery: {
      handler() { this.debouncedSearch() },
      immediate: false
    }
  },

  mounted() {
    this.loadServiceProviders()
    this.loadAllData()
  },

  methods: {
    buildQueryString(params) {
      return Object.keys(params)
        .map(key => `${encodeURIComponent(key)}=${encodeURIComponent(params[key])}`)
        .join('&')
    },

    debouncedSearch: (function() {
      let timeout = null
      return function() {
        clearTimeout(timeout)
        timeout = setTimeout(() => {
          this.loadAllData()
        }, 500)
      }
    })(),

    async loadServiceProviders() {
      const baseURL = this.appConfig.xdrAPI.baseURL || 'http://localhost:5000'
      try {
        const response = await axios.get(`${baseURL}/prismacloud/vulnerabilities/service-providers`)
        if (response.data && response.data.service_providers) {
          this.serviceProviders = [
            { value: 'all', text: 'All Providers' },
            ...response.data.service_providers.map(sp => ({ value: sp, text: sp }))
          ]
        }
      } catch (error) {
        console.error('Error loading service providers:', error)
      }
    },

    async loadAllData() {
      this.loading = true
      this.tableLoading = true
      const baseURL = this.appConfig.xdrAPI.baseURL || 'http://localhost:5000'
      const queryString = this.buildQueryString(this.filterParams)

      try {
        // Load all data in parallel
        const [
          statsResponse,
          severityResponse,
          topCvesResponse,
          topAssetsResponse,
          topPackagesResponse,
          attackVectorsResponse,
          assetTypesResponse,
          trendsResponse,
          vulnerabilitiesResponse
        ] = await Promise.all([
          axios.get(`${baseURL}/prismacloud/vulnerabilities/stats?${queryString}`),
          axios.get(`${baseURL}/prismacloud/vulnerabilities/severity-distribution?${queryString}`),
          axios.get(`${baseURL}/prismacloud/vulnerabilities/top-cves?${queryString}`),
          axios.get(`${baseURL}/prismacloud/vulnerabilities/top-assets?${queryString}`),
          axios.get(`${baseURL}/prismacloud/vulnerabilities/top-packages?${queryString}`),
          axios.get(`${baseURL}/prismacloud/vulnerabilities/attack-vectors?${queryString}`),
          axios.get(`${baseURL}/prismacloud/vulnerabilities/asset-types?${queryString}`),
          axios.get(`${baseURL}/prismacloud/vulnerabilities/trends?${queryString}`),
          axios.get(`${baseURL}/prismacloud/vulnerabilities?${queryString}&page=${this.currentPage}&page_size=${this.pageSize}`)
        ])

        // Process stats
        if (statsResponse.data) {
          this.stats = statsResponse.data
        }

        // Process severity distribution
        if (severityResponse.data && severityResponse.data.distribution) {
          this.renderSeverityChart(severityResponse.data.distribution)
        }

        // Process top CVEs
        if (topCvesResponse.data && topCvesResponse.data.top_cves) {
          this.renderTopCvesChart(topCvesResponse.data.top_cves)
        }

        // Process top assets
        if (topAssetsResponse.data && topAssetsResponse.data.top_assets) {
          this.renderTopAssetsChart(topAssetsResponse.data.top_assets)
        }

        // Process top packages
        if (topPackagesResponse.data && topPackagesResponse.data.top_packages) {
          this.renderTopPackagesChart(topPackagesResponse.data.top_packages)
        }

        // Process attack vectors
        if (attackVectorsResponse.data && attackVectorsResponse.data.attack_vectors) {
          this.renderAttackVectorChart(attackVectorsResponse.data.attack_vectors)
        }

        // Process asset types
        if (assetTypesResponse.data && assetTypesResponse.data.asset_types) {
          this.renderAssetTypeChart(assetTypesResponse.data.asset_types)
        }

        // Process trends
        if (trendsResponse.data && trendsResponse.data.trends) {
          this.renderTrendsChart(trendsResponse.data.trends)
        }

        // Process vulnerabilities table
        if (vulnerabilitiesResponse.data) {
          this.vulnerabilities = vulnerabilitiesResponse.data.vulnerabilities || []
          this.totalCount = vulnerabilitiesResponse.data.total_count || 0
          this.totalPages = vulnerabilitiesResponse.data.total_pages || 0
        }

        this.updated = new Date().toLocaleString()
      } catch (error) {
        console.error('Error loading data:', error)
      } finally {
        this.loading = false
        this.tableLoading = false
      }
    },

    renderSeverityChart(distribution) {
      const severityOrder = ['critical', 'high', 'medium', 'low']
      const colorMap = {
        critical: '#802020',
        high: '#DC001E',
        medium: '#FECA00',
        low: '#01AD00'
      }

      const sorted = severityOrder
        .map(sev => distribution.find(d => d._id === sev))
        .filter(d => d)

      this.severityChartOptions = {
        ...this.severityChartOptions,
        labels: sorted.map(d => this.capitalize(d._id)),
        colors: sorted.map(d => colorMap[d._id] || '#6c757d')
      }
      this.severityChartSeries = sorted.map(d => d.count)
    },

    renderTopCvesChart(topCves) {
      const colorMap = {
        critical: '#802020',
        high: '#DC001E',
        medium: '#FECA00',
        low: '#01AD00'
      }

      this.topCvesChartOptions = {
        ...this.topCvesChartOptions,
        xaxis: {
          ...this.topCvesChartOptions.xaxis,
          categories: topCves.map(cve => this.truncateText(cve._id, 20))
        },
        colors: topCves.map(cve => colorMap[cve.severity] || '#6c757d')
      }
      this.topCvesChartSeries = [{
        name: 'Count',
        data: topCves.map(cve => cve.count)
      }]
      this.topCves = topCves
    },

    renderTopAssetsChart(topAssets) {
      this.topAssetsChartOptions = {
        ...this.topAssetsChartOptions,
        xaxis: {
          ...this.topAssetsChartOptions.xaxis,
          categories: topAssets.map(asset => this.truncateText(asset._id, 25))
        },
        colors: topAssets.map(asset => {
          if (asset.critical_count > 0) return '#802020'
          if (asset.high_count > 0) return '#DC001E'
          return '#FECA00'
        })
      }
      this.topAssetsChartSeries = [{
        name: 'Vulnerabilities',
        data: topAssets.map(asset => asset.vuln_count)
      }]
      this.topAssets = topAssets
    },

    renderTopPackagesChart(topPackages) {
      this.topPackagesChartOptions = {
        ...this.topPackagesChartOptions,
        xaxis: {
          ...this.topPackagesChartOptions.xaxis,
          categories: topPackages.map(pkg => this.truncateText(pkg._id, 25))
        },
        colors: topPackages.map(pkg => {
          if (pkg.critical_count > 0) return '#802020'
          if (pkg.high_count > 0) return '#DC001E'
          return '#FECA00'
        })
      }
      this.topPackagesChartSeries = [{
        name: 'Vulnerabilities',
        data: topPackages.map(pkg => pkg.count)
      }]
      this.topPackages = topPackages
    },

    renderAttackVectorChart(attackVectors) {
      const colorMap = {
        'Network': '#5470C6',
        'Local': '#73C0DE',
        'Adjacent': '#9A60B4',
        'Physical': '#EA7CCC'
      }

      this.attackVectorChartOptions = {
        ...this.attackVectorChartOptions,
        labels: attackVectors.map(av => av._id || 'Unknown'),
        colors: attackVectors.map(av => colorMap[av._id] || '#6c757d')
      }
      this.attackVectorChartSeries = attackVectors.map(av => av.count)
    },

    renderAssetTypeChart(assetTypes) {
      this.assetTypeChartOptions = {
        ...this.assetTypeChartOptions,
        labels: assetTypes.map(at => this.capitalize(at._id || 'Unknown'))
      }
      this.assetTypeChartSeries = assetTypes.map(at => at.count)
    },

    renderTrendsChart(trends) {
      this.trendsChartOptions = {
        ...this.trendsChartOptions,
        xaxis: {
          ...this.trendsChartOptions.xaxis,
          categories: trends.map(t => t._id)
        }
      }
      this.trendsChartSeries = [
        { name: 'Total', data: trends.map(t => t.count) },
        { name: 'Critical', data: trends.map(t => t.critical_count) },
        { name: 'High', data: trends.map(t => t.high_count) }
      ]
    },

    truncateText(text, maxLength) {
      if (!text) return ''
      return text.length > maxLength ? text.substring(0, maxLength) + '...' : text
    },

    capitalize(str) {
      if (!str) return ''
      return str.charAt(0).toUpperCase() + str.slice(1)
    },

    getSeverityClass(severity) {
      const classes = {
        critical: 'badge badge-severity-critical',
        high: 'badge badge-severity-high',
        medium: 'badge badge-severity-medium',
        low: 'badge badge-severity-low'
      }
      const key = severity ? severity.toLowerCase() : ''
      return classes[key] || 'badge bg-secondary'
    },

    getYesNoClass(value) {
      return value === 'Y' ? 'badge badge-yes' : 'badge badge-no'
    },

    formatDate(dateStr) {
      if (!dateStr) return 'N/A'
      try {
        const date = new Date(dateStr.$date || dateStr)
        return date.toLocaleDateString()
      } catch (e) {
        return 'N/A'
      }
    },

    toggleSort(key) {
      const column = this.columns.find(col => col.key === key)
      if (!column || !column.sortable) return

      if (this.sortKey === key) {
        this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc'
      } else {
        this.sortKey = key
        this.sortDirection = 'desc'
      }

      this.loadAllData()
    },

    getSortIcon(key) {
      if (this.sortKey !== key) return 'mdi mdi-sort'
      return this.sortDirection === 'asc' ? 'mdi mdi-sort-ascending' : 'mdi mdi-sort-descending'
    },

    goToPage(page) {
      if (page < 1 || page > this.totalPages) return
      this.currentPage = page
      this.loadAllData()
    },

    getSeverityColorClass(severity) {
      const classes = {
        critical: 'text-danger',
        high: 'text-warning',
        medium: 'text-info',
        low: 'text-success'
      }
      const key = severity ? severity.toLowerCase() : ''
      return classes[key] || 'text-secondary'
    },

    getSeverityCheckboxClass(severity) {
      const classes = {
        critical: 'severity-critical',
        high: 'severity-high',
        medium: 'severity-medium',
        low: 'severity-low'
      }
      const key = severity ? severity.toLowerCase() : ''
      return classes[key] || ''
    },

    async getChartDataURI(chartRef) {
      if (chartRef && chartRef.chart) {
        try {
          // Use JPEG with lower scale to reduce file size
          const result = await chartRef.chart.dataURI({ scale: 1.5 })
          return result.imgURI
        } catch (e) {
          console.error('Error exporting chart:', e)
          return null
        }
      }
      return null
    },

    async downloadPdf() {
      // Create PDF document (A4 Landscape: 297mm x 210mm)
      const doc = new jsPDF({
        orientation: 'landscape',
        unit: 'mm',
        format: 'a4'
      })

      const pageWidth = 297
      const pageHeight = 210
      const margin = 12
      const DEBUG_BORDERS = false

      // Helper function to draw debug borders
      const drawDebugBorder = (x, y, width, height, color = [255, 0, 0], label = '') => {
        if (!DEBUG_BORDERS) return

        doc.setDrawColor(...color)
        doc.setLineWidth(0.5)
        doc.rect(x, y, width, height)

        if (label) {
          doc.setFontSize(6)
          doc.setTextColor(...color)
          doc.text(`${label} (${width.toFixed(1)}x${height.toFixed(1)}mm)`, x + 2, y + height - 2)
        }
      }

      // Colors
      const colors = {
        primary: [85, 110, 230],
        danger: [220, 53, 69],
        warning: [255, 193, 7],
        info: [23, 162, 184],
        success: [40, 167, 69],
        secondary: [108, 117, 125],
        dark: [52, 58, 64],
        light: [248, 249, 250]
      }

      // ==================== PAGE 1 ====================
      // Draw page boundary (for debugging)
      drawDebugBorder(0, 0, pageWidth, pageHeight, [200, 200, 200], 'Page')
      drawDebugBorder(margin, margin, pageWidth - 2 * margin, pageHeight - 2 * margin, [150, 150, 150], 'Content Area')

      // Title
      doc.setFontSize(18)
      doc.setTextColor(...colors.dark)
      doc.text('Prisma Cloud Vulnerabilities Report', margin, 15)

      // Date
      doc.setFontSize(9)
      doc.setTextColor(...colors.secondary)
      doc.text(`Generated: ${new Date().toLocaleString()}`, margin, 22)

      // Stats Row - optimized for better spacing
      const statsY = 30
      const statsHeight = 20
      const statsWidth = 43
      const statsGap = 2
      const statsData = [
        { label: 'Total Vulnerabilities', value: this.stats.total_count, color: colors.primary },
        { label: 'Critical', value: this.stats.critical_count, color: colors.danger },
        { label: 'Exploitable', value: this.stats.exploitable_count, color: colors.warning },
        { label: 'Internet Exposed', value: this.stats.internet_exposed_count, color: colors.info },
        { label: 'Unique Assets', value: this.stats.unique_assets, color: colors.secondary },
        { label: 'Unique CVEs', value: this.stats.unique_cves, color: colors.success }
      ]

      statsData.forEach((stat, index) => {
        const x = margin + (index * (statsWidth + statsGap))

        // Card background
        doc.setFillColor(248, 249, 250)
        doc.roundedRect(x, statsY, statsWidth, statsHeight, 2, 2, 'F')

        // Color indicator bar
        doc.setFillColor(...stat.color)
        doc.rect(x, statsY, 3, statsHeight, 'F')

        // Label
        doc.setFontSize(7)
        doc.setTextColor(...colors.secondary)
        doc.text(stat.label, x + 5, statsY + 6)

        // Value
        doc.setFontSize(13)
        doc.setTextColor(...colors.dark)
        doc.text(stat.value.toLocaleString(), x + 5, statsY + 15)

        // Debug border for stats card
        drawDebugBorder(x, statsY, statsWidth, statsHeight, [128, 0, 128], `Stat${index + 1}`)
      })

      // Charts Row 1 (Donut/Pie charts) - properly justified within margins
      const chartsY = 65
      const donutChartSize = 85  // Square charts at 85x85mm
      const availableWidth = pageWidth - 2 * margin  // 273mm available
      const donutChartGap = (availableWidth - 3 * donutChartSize) / 2  // Equal gaps between 3 charts

      // Export and add charts
      const severityImg = await this.getChartDataURI(this.$refs.severityChart)
      const attackVectorImg = await this.getChartDataURI(this.$refs.attackVectorChart)
      const assetTypeImg = await this.getChartDataURI(this.$refs.assetTypeChart)

      // Chart 1: Severity Distribution
      const chart1X = margin
      if (severityImg) {
        doc.setFontSize(10)
        doc.setTextColor(...colors.dark)
        doc.text('Severity Distribution', chart1X, chartsY - 4)
        doc.addImage(severityImg, 'JPEG', chart1X, chartsY, donutChartSize * 1.5, donutChartSize)
        drawDebugBorder(chart1X, chartsY, donutChartSize, donutChartSize, [255, 0, 0], 'Severity')
      }

      // Chart 2: Attack Vector Distribution
      const chart2X = margin + donutChartSize + donutChartGap
      if (attackVectorImg) {
        doc.setFontSize(10)
        doc.setTextColor(...colors.dark)
        doc.text('Attack Vector Distribution', chart2X, chartsY - 4)
        doc.addImage(attackVectorImg, 'JPEG', chart2X, chartsY, donutChartSize * 1.5, donutChartSize)
        drawDebugBorder(chart2X, chartsY, donutChartSize, donutChartSize, [0, 255, 0], 'Attack Vector')
      }

      // Chart 3: Asset Type Distribution
      const chart3X = margin + 2 * (donutChartSize + donutChartGap)
      if (assetTypeImg) {
        doc.setFontSize(10)
        doc.setTextColor(...colors.dark)
        doc.text('Asset Type Distribution', chart3X, chartsY - 4)
        doc.addImage(assetTypeImg, 'JPEG', chart3X, chartsY, donutChartSize * 1.5, donutChartSize)
        drawDebugBorder(chart3X, chartsY, donutChartSize, donutChartSize, [0, 0, 255], 'Asset Type')
      }

      // Filter summary at bottom of page 1
      doc.setFontSize(8)
      doc.setTextColor(...colors.secondary)
      const filterText = `Filters: Date Range: Last ${this.selectedDateRange} days | Severities: ${this.selectedSeverities.join(', ') || 'All'}`
      doc.text(filterText, margin, pageHeight - 6)

      // ==================== PAGE 2 ====================
      doc.addPage()

      // Draw page boundary (for debugging)
      drawDebugBorder(0, 0, pageWidth, pageHeight, [200, 200, 200], 'Page 2')
      drawDebugBorder(margin, margin, pageWidth - 2 * margin, pageHeight - 2 * margin, [150, 150, 150], 'Content Area 2')

      // Larger bar charts to fill the page better - stacked layout for better use of space
      const barChartWidth = (pageWidth - 2 * margin - 8) / 2  // Use almost full width, split in 2
      const barChartHeight = 85  // Height for each row of charts
      const barChartY1 = 20  // First row
      const barChartY2 = barChartY1 + barChartHeight + 18  // Second row (with gap)
      const barChartGap = 8

      // Top CVEs Chart
      const topCvesImg = await this.getChartDataURI(this.$refs.topCvesChart)
      if (topCvesImg) {
        doc.setFontSize(11)
        doc.setTextColor(...colors.dark)
        doc.text('Top 10 CVEs by Count', margin, 15)
        doc.addImage(topCvesImg, 'JPEG', margin, barChartY1, barChartWidth * 1.5, barChartHeight * 1.5)
        drawDebugBorder(margin, barChartY1, barChartWidth, barChartHeight, [255, 0, 0], 'Top CVEs')
      }

      // Top Assets Chart
      const topAssetsImg = await this.getChartDataURI(this.$refs.topAssetsChart)
      if (topAssetsImg) {
        doc.setFontSize(11)
        doc.setTextColor(...colors.dark)
        doc.text('Top 10 Vulnerable Assets', margin + barChartWidth + barChartGap, 15)
        doc.addImage(topAssetsImg, 'JPEG', margin + barChartWidth + barChartGap, barChartY1, barChartWidth * 1.5, barChartHeight * 1.5)
        drawDebugBorder(margin + barChartWidth + barChartGap, barChartY1, barChartWidth, barChartHeight, [0, 255, 0], 'Top Assets')
      }

      // Top Packages Chart (second row)
      const topPackagesImg = await this.getChartDataURI(this.$refs.topPackagesChart)
      if (topPackagesImg) {
        doc.setFontSize(11)
        doc.setTextColor(...colors.dark)
        doc.text('Top 10 Vulnerable Packages', margin, barChartY2 - 5)
        doc.addImage(topPackagesImg, 'JPEG', margin, barChartY2, barChartWidth * 1.5, barChartHeight * 1.5  )
        drawDebugBorder(margin, barChartY2, barChartWidth, barChartHeight, [0, 0, 255], 'Top Packages')
      }

      // Trends Chart (second row)
      const trendsImg = await this.getChartDataURI(this.$refs.trendsChart)
      if (trendsImg) {
        doc.setFontSize(11)
        doc.setTextColor(...colors.dark)
        doc.text('Vulnerability Trends', margin + barChartWidth + barChartGap, barChartY2 - 5)
        doc.addImage(trendsImg, 'JPEG', margin + barChartWidth + barChartGap, barChartY2, barChartWidth * 1.5, barChartHeight * 1.5)
        drawDebugBorder(margin + barChartWidth + barChartGap, barChartY2, barChartWidth, barChartHeight, [255, 165, 0], 'Trends')
      }

      // Save the PDF
      doc.save('Prisma Cloud Vulnerabilities Report.pdf')
    },

    async exportToCsv() {
      this.exporting = true
      const baseURL = this.appConfig.xdrAPI.baseURL || 'http://localhost:5000'
      const queryString = this.buildQueryString(this.filterParams)

      try {
        // Fetch all vulnerabilities (no pagination)
        const response = await axios.get(
          `${baseURL}/prismacloud/vulnerabilities?${queryString}&page=1&page_size=${this.totalCount || 10000}`
        )

        if (!response.data || !response.data.vulnerabilities) {
          console.error('No data to export')
          return
        }

        const vulnerabilities = response.data.vulnerabilities

        // Define CSV headers
        const headers = [
          'CVE ID',
          'Asset Name',
          'Asset Type',
          'Owner',
          'Severity',
          'CVSS Score',
          'Package Name',
          'Patchable',
          'Exploitable',
          'Internet Exposed',
          'Attack Vector',
          'Discovered Date',
          'Service Provider'
        ]

        // Convert data to CSV rows
        const rows = vulnerabilities.map(vuln => [
          vuln.CVE_ID || '',
          vuln.ASSET_NAME || '',
          vuln.ASSET_TYPE || '',
          vuln.OWNER || '',
          vuln.SEVERITY || '',
          vuln.CVSS_BASE_SCORE || '',
          vuln.PACKAGE_NAME || '',
          vuln.PATCHABLE || '',
          vuln.EXPLOITABLE || '',
          vuln.INTERNET_EXPOSED || '',
          vuln.ATTACK_VECTOR || '',
          this.formatDate(vuln.DISCOVERED_DATE) || '',
          vuln.SERVICE_PROVIDER || ''
        ])

        // Build CSV content
        const csvContent = [
          headers.join(','),
          ...rows.map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
        ].join('\n')

        // Create and download file
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
        const link = document.createElement('a')
        const url = URL.createObjectURL(blob)
        link.setAttribute('href', url)
        link.setAttribute('download', `prismacloud_vulnerabilities_${new Date().toISOString().split('T')[0]}.csv`)
        link.style.visibility = 'hidden'
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
      } catch (error) {
        console.error('Error exporting CSV:', error)
      } finally {
        this.exporting = false
      }
    }
  }
}
</script>

<template>
  <Layout>
    <PageHeader :title="title" :items="items" />
    <div class="container" style="max-width: 1600px !important;">
      <!-- Download Buttons -->
      <div class="mb-3">
        <b-button
          class="btn btn-primary btn-sm"
          variant="light"
          style="margin-right: 12px;"
          @click="downloadPdf"
        >
          <feather type="download" class="icon-xs mr-1"></feather>
          Download PDF
        </b-button>
        <b-button
          class="btn btn-primary btn-sm"
          variant="light"
          :disabled="exporting || totalCount === 0"
          @click="exportToCsv"
        >
          <feather type="file-text" class="icon-xs mr-1"></feather>
          <span v-if="exporting">Exporting...</span>
          <span v-else>Export CSV</span>
        </b-button>
      </div>

        <!-- Filters Panel -->
        <div class="card mb-4 filter-panel">
        <div class="card-body py-3">
          <!-- Row 1: Main filters -->
          <div class="row g-3 align-items-end">
            <!-- Search -->
            <div class="col-lg-2 col-md-4">
              <label class="filter-label">Search</label>
              <input
                type="text"
                class="form-control form-control-sm"
                v-model="searchQuery"
                placeholder="CVE, Asset, Package, Owner..."
              >
            </div>

            <!-- Date Range -->
            <div class="col-lg-2 col-md-4">
              <label class="filter-label">Date Range</label>
              <select class="form-select form-select-sm" v-model="selectedDateRange">
                <option v-for="opt in dateRangeOptions" :key="opt.value" :value="opt.value">
                  {{ opt.text }}
                </option>
              </select>
            </div>

            <!-- Asset Type -->
            <div class="col-lg-2 col-md-4">
              <label class="filter-label">Asset Type</label>
              <select class="form-select form-select-sm" v-model="selectedAssetType">
                <option v-for="opt in assetTypeOptions" :key="opt.value" :value="opt.value">
                  {{ opt.text }}
                </option>
              </select>
            </div>

            <!-- Service Provider -->
            <div class="col-lg-2 col-md-4">
              <label class="filter-label">Service Provider</label>
              <select class="form-select form-select-sm" v-model="selectedServiceProvider">
                <option v-for="opt in serviceProviders" :key="opt.value" :value="opt.value">
                  {{ opt.text }}
                </option>
              </select>
            </div>

            <!-- Severity Checkboxes -->
            <div class="col-lg-4 col-md-8">
              <label class="filter-label">Severity</label>
              <div class="severity-filters">
                <label
                  v-for="opt in severityOptions"
                  :key="opt.value"
                  class="severity-checkbox"
                  :class="[getSeverityCheckboxClass(opt.value), { 'is-checked': selectedSeverities.includes(opt.value) }]"
                >
                  <input
                    type="checkbox"
                    :value="opt.value"
                    v-model="selectedSeverities"
                  >
                  {{ opt.text }}
                </label>
              </div>
            </div>
          </div>

          <!-- Row 2: Secondary filters -->
          <div class="row g-3 align-items-end mt-1">
            <!-- Patchable -->
            <div class="col-lg-2 col-md-4">
              <label class="filter-label">Patchable</label>
              <select class="form-select form-select-sm" v-model="selectedPatchable">
                <option v-for="opt in patchableOptions" :key="opt.value" :value="opt.value">
                  {{ opt.text }}
                </option>
              </select>
            </div>

            <!-- Exploitable -->
            <div class="col-lg-2 col-md-4">
              <label class="filter-label">Exploitable</label>
              <select class="form-select form-select-sm" v-model="selectedExploitable">
                <option v-for="opt in exploitableOptions" :key="opt.value" :value="opt.value">
                  {{ opt.text }}
                </option>
              </select>
            </div>

            <!-- CISA KEV -->
            <div class="col-lg-2 col-md-4">
              <label class="filter-label">CISA KEV</label>
              <select class="form-select form-select-sm" v-model="selectedCisaKev">
                <option v-for="opt in cisaKevOptions" :key="opt.value" :value="opt.value">
                  {{ opt.text }}
                </option>
              </select>
            </div>

            <!-- Internet Exposed -->
            <div class="col-lg-2 col-md-4">
              <label class="filter-label">Internet Exposed</label>
              <select class="form-select form-select-sm" v-model="selectedInternetExposed">
                <option v-for="opt in internetExposedOptions" :key="opt.value" :value="opt.value">
                  {{ opt.text }}
                </option>
              </select>
            </div>

            <!-- Updated timestamp -->
            <div class="col-lg-4 col-md-8 text-end">
              <span class="updated-text">Updated: {{ updated }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Loading -->
      <div v-if="loading" class="text-center py-5">
        <div class="spinner-border" role="status">
          <span class="sr-only">Loading...</span>
        </div>
        <p class="mt-2">Loading dashboard data...</p>
      </div>

      <div v-else>
        <!-- Stats Cards -->
        <div class="row mb-4 no-break">
          <div class="col-md-2">
            <div class="card mini-stats-wid no-break">
              <div class="card-body">
                <div class="d-flex">
                  <div class="flex-grow-1">
                    <p class="text-muted fw-medium mb-2">Total Vulnerabilities</p>
                    <h4 class="mb-0">{{ stats.total_count.toLocaleString() }}</h4>
                  </div>
                  <div class="mini-stat-icon avatar-sm align-self-center rounded-circle bg-primary">
                    <span class="avatar-title">
                      <feather type="shield" class="icon-sm"></feather>
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="col-md-2">
            <div class="card mini-stats-wid">
              <div class="card-body">
                <div class="d-flex">
                  <div class="flex-grow-1">
                    <p class="text-muted fw-medium mb-2">Critical</p>
                    <h4 class="mb-0 text-danger">{{ stats.critical_count.toLocaleString() }}</h4>
                  </div>
                  <div class="mini-stat-icon avatar-sm align-self-center rounded-circle bg-danger">
                    <span class="avatar-title">
                      <feather type="alert-octagon" class="icon-sm"></feather>
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="col-md-2">
            <div class="card mini-stats-wid">
              <div class="card-body">
                <div class="d-flex">
                  <div class="flex-grow-1">
                    <p class="text-muted fw-medium mb-2">Exploitable</p>
                    <h4 class="mb-0 text-warning">{{ stats.exploitable_count.toLocaleString() }}</h4>
                  </div>
                  <div class="mini-stat-icon avatar-sm align-self-center rounded-circle bg-warning">
                    <span class="avatar-title">
                      <feather type="zap" class="icon-sm"></feather>
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="col-md-2">
            <div class="card mini-stats-wid">
              <div class="card-body">
                <div class="d-flex">
                  <div class="flex-grow-1">
                    <p class="text-muted fw-medium mb-2">Internet Exposed</p>
                    <h4 class="mb-0 text-info">{{ stats.internet_exposed_count.toLocaleString() }}</h4>
                  </div>
                  <div class="mini-stat-icon avatar-sm align-self-center rounded-circle bg-info">
                    <span class="avatar-title">
                      <feather type="globe" class="icon-sm"></feather>
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="col-md-2">
            <div class="card mini-stats-wid">
              <div class="card-body">
                <div class="d-flex">
                  <div class="flex-grow-1">
                    <p class="text-muted fw-medium mb-2">Unique Assets</p>
                    <h4 class="mb-0">{{ stats.unique_assets.toLocaleString() }}</h4>
                  </div>
                  <div class="mini-stat-icon avatar-sm align-self-center rounded-circle bg-secondary">
                    <span class="avatar-title">
                      <feather type="server" class="icon-sm"></feather>
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="col-md-2">
            <div class="card mini-stats-wid">
              <div class="card-body">
                <div class="d-flex">
                  <div class="flex-grow-1">
                    <p class="text-muted fw-medium mb-2">Unique CVEs</p>
                    <h4 class="mb-0">{{ stats.unique_cves.toLocaleString() }}</h4>
                  </div>
                  <div class="mini-stat-icon avatar-sm align-self-center rounded-circle bg-success">
                    <span class="avatar-title">
                      <feather type="file-text" class="icon-sm"></feather>
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Charts Row 1 -->
        <div class="row mb-4">
          <div class="col-md-4">
            <div class="card">
              <div class="card-body">
                <h5 class="card-title">Severity Distribution</h5>
                <apexchart
                  ref="severityChart"
                  v-if="severityChartSeries.length > 0"
                  height="280"
                  :options="severityChartOptions"
                  :series="severityChartSeries"
                ></apexchart>
                <div v-else class="text-center py-5 text-muted">No data available</div>
              </div>
            </div>
          </div>

          <div class="col-md-4">
            <div class="card">
              <div class="card-body">
                <h5 class="card-title">Attack Vector Distribution</h5>
                <apexchart
                  ref="attackVectorChart"
                  v-if="attackVectorChartSeries.length > 0"
                  height="280"
                  :options="attackVectorChartOptions"
                  :series="attackVectorChartSeries"
                ></apexchart>
                <div v-else class="text-center py-5 text-muted">No data available</div>
              </div>
            </div>
          </div>

          <div class="col-md-4">
            <div class="card">
              <div class="card-body">
                <h5 class="card-title">Asset Type Distribution</h5>
                <apexchart
                  ref="assetTypeChart"
                  v-if="assetTypeChartSeries.length > 0"
                  height="280"
                  :options="assetTypeChartOptions"
                  :series="assetTypeChartSeries"
                ></apexchart>
                <div v-else class="text-center py-5 text-muted">No data available</div>
              </div>
            </div>
          </div>
        </div>

        <!-- Charts Row 2 -->
        <div class="row mb-4">
          <div class="col-md-6">
            <div class="card">
              <div class="card-body">
                <div class="d-flex justify-content-between align-items-center mb-2">
                  <h5 class="card-title mb-0">Top 10 CVEs by Count</h5>
                  <div class="severity-legend">
                    <span class="legend-item"><span class="legend-color bg-severity-critical"></span>Critical</span>
                    <span class="legend-item"><span class="legend-color bg-severity-high"></span>High</span>
                    <span class="legend-item"><span class="legend-color bg-severity-medium"></span>Medium</span>
                  </div>
                </div>
                <apexchart
                  ref="topCvesChart"
                  v-if="topCvesChartSeries.length > 0"
                  height="320"
                  :options="topCvesChartOptions"
                  :series="topCvesChartSeries"
                ></apexchart>
                <div v-else class="text-center py-5 text-muted">No data available</div>
              </div>
            </div>
          </div>

          <div class="col-md-6">
            <div class="card">
              <div class="card-body">
                <div class="d-flex justify-content-between align-items-center mb-2">
                  <h5 class="card-title mb-0">Top 10 Vulnerable Assets</h5>
                  <div class="severity-legend">
                    <span class="legend-item"><span class="legend-color bg-severity-critical"></span>Critical</span>
                    <span class="legend-item"><span class="legend-color bg-severity-high"></span>High</span>
                    <span class="legend-item"><span class="legend-color bg-severity-medium"></span>Medium</span>
                  </div>
                </div>
                <apexchart
                  ref="topAssetsChart"
                  v-if="topAssetsChartSeries.length > 0"
                  height="320"
                  :options="topAssetsChartOptions"
                  :series="topAssetsChartSeries"
                ></apexchart>
                <div v-else class="text-center py-5 text-muted">No data available</div>
              </div>
            </div>
          </div>
        </div>

        <!-- Charts Row 3 -->
        <div class="row mb-4">
          <div class="col-md-6">
            <div class="card">
              <div class="card-body">
                <div class="d-flex justify-content-between align-items-center mb-2">
                  <h5 class="card-title mb-0">Top 10 Vulnerable Packages</h5>
                  <div class="severity-legend">
                    <span class="legend-item"><span class="legend-color bg-severity-critical"></span>Critical</span>
                    <span class="legend-item"><span class="legend-color bg-severity-high"></span>High</span>
                    <span class="legend-item"><span class="legend-color bg-severity-medium"></span>Medium</span>
                  </div>
                </div>
                <apexchart
                  ref="topPackagesChart"
                  v-if="topPackagesChartSeries.length > 0"
                  height="320"
                  :options="topPackagesChartOptions"
                  :series="topPackagesChartSeries"
                ></apexchart>
                <div v-else class="text-center py-5 text-muted">No data available</div>
              </div>
            </div>
          </div>

          <div class="col-md-6">
            <div class="card">
              <div class="card-body">
                <h5 class="card-title">Vulnerability Trends</h5>
                <apexchart
                  ref="trendsChart"
                  v-if="trendsChartSeries.length > 0"
                  height="320"
                  :options="trendsChartOptions"
                  :series="trendsChartSeries"
                ></apexchart>
                <div v-else class="text-center py-5 text-muted">No data available</div>
              </div>
            </div>
          </div>
        </div>
      </div><!-- End v-else -->

      <!-- Vulnerabilities Table -->
      <div class="card position-relative">
        <!-- Table Loading Overlay -->
        <div v-if="tableLoading" class="table-loading-overlay">
          <div class="spinner-border text-primary" role="status">
            <span class="sr-only">Loading...</span>
          </div>
          <p class="mt-2 mb-0 text-muted">Loading vulnerabilities...</p>
        </div>
        <div class="card-body">
          <div class="d-flex justify-content-between align-items-center mb-3">
            <h5 class="card-title mb-0">Vulnerabilities ({{ totalCount.toLocaleString() }} total)</h5>
            <div>
              <span class="text-muted me-3">
                Page {{ currentPage }} of {{ totalPages }}
              </span>
              <button
                class="btn btn-sm btn-outline-secondary me-1"
                :disabled="currentPage === 1"
                @click="goToPage(currentPage - 1)"
              >
                Previous
              </button>
              <button
                class="btn btn-sm btn-outline-secondary"
                :disabled="currentPage >= totalPages"
                @click="goToPage(currentPage + 1)"
              >
                Next
              </button>
            </div>
          </div>

          <div class="table-responsive">
            <table class="table table-striped table-hover">
              <thead>
                <tr>
                  <th
                    v-for="col in columns"
                    :key="col.key"
                    :class="{ 'sortable': col.sortable, 'active': sortKey === col.key }"
                    @click="col.sortable && toggleSort(col.key)"
                    style="cursor: pointer;"
                  >
                    {{ col.label }}
                    <i v-if="col.sortable" :class="getSortIcon(col.key)" class="ms-1"></i>
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(vuln, index) in vulnerabilities" :key="index">
                  <td class="col-cve">
                    <a v-if="vuln.CVE_LINK && vuln.CVE_LINK !== 'N/A'" :href="vuln.CVE_LINK" target="_blank">
                      {{ vuln.CVE_ID }}
                    </a>
                    <span v-else>{{ vuln.CVE_ID }}</span>
                  </td>
                  <td class="col-asset">
                    <span v-b-tooltip.hover.top :title="vuln.ASSET_NAME || ''">{{ truncateText(vuln.ASSET_NAME, 25) }}</span>
                  </td>
                  <td class="col-type">{{ vuln.ASSET_TYPE }}</td>
                  <td class="col-owner">
                    <span v-b-tooltip.hover.top :title="vuln.OWNER || 'N/A'">{{ truncateText(vuln.OWNER, 15) || 'N/A' }}</span>
                  </td>
                  <td class="col-severity"><span :class="getSeverityClass(vuln.SEVERITY)">{{ capitalize(vuln.SEVERITY) }}</span></td>
                  <td class="col-cvss">{{ vuln.CVSS_BASE_SCORE || 'N/A' }}</td>
                  <td class="col-package">
                    <span v-b-tooltip.hover.top :title="vuln.PACKAGE_NAME || ''">{{ truncateText(vuln.PACKAGE_NAME, 15) }}</span>
                  </td>
                  <td class="col-flag"><span :class="getYesNoClass(vuln.PATCHABLE)">{{ vuln.PATCHABLE }}</span></td>
                  <td class="col-flag"><span :class="getYesNoClass(vuln.EXPLOITABLE)">{{ vuln.EXPLOITABLE }}</span></td>
                  <td class="col-flag"><span :class="getYesNoClass(vuln.INTERNET_EXPOSED)">{{ vuln.INTERNET_EXPOSED }}</span></td>
                  <td class="col-vector">{{ vuln.ATTACK_VECTOR || 'N/A' }}</td>
                  <td class="col-date">{{ formatDate(vuln.DISCOVERED_DATE) }}</td>
                </tr>
                <tr v-if="vulnerabilities.length === 0">
                  <td :colspan="columns.length" class="text-center py-4">
                    No vulnerabilities found matching your criteria.
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- Pagination -->
          <div class="d-flex justify-content-center mt-3" v-if="totalPages > 1">
            <nav>
              <ul class="pagination pagination-sm mb-0">
                <li class="page-item" :class="{ disabled: currentPage === 1 }">
                  <a class="page-link" href="#" @click.prevent="goToPage(1)">First</a>
                </li>
                <li class="page-item" :class="{ disabled: currentPage === 1 }">
                  <a class="page-link" href="#" @click.prevent="goToPage(currentPage - 1)">Prev</a>
                </li>
                <li
                  v-for="page in Math.min(5, totalPages)"
                  :key="page"
                  class="page-item"
                  :class="{ active: currentPage === page }"
                >
                  <a class="page-link" href="#" @click.prevent="goToPage(page)">{{ page }}</a>
                </li>
                <li class="page-item" :class="{ disabled: currentPage >= totalPages }">
                  <a class="page-link" href="#" @click.prevent="goToPage(currentPage + 1)">Next</a>
                </li>
                <li class="page-item" :class="{ disabled: currentPage >= totalPages }">
                  <a class="page-link" href="#" @click.prevent="goToPage(totalPages)">Last</a>
                </li>
              </ul>
            </nav>
          </div>
        </div>
      </div>
    </div>
  </Layout>
</template>

<style scoped>
/* Button icon styling */
.icon-xs {
  width: 14px;
  height: 14px;
  vertical-align: middle;
}

.mr-1 {
  margin-right: 0.25rem;
}

/* Filter Panel Styles */
.filter-panel {
  background: #fff;
  border: 1px solid #e9ecef;
}

.filter-panel .card-body {
  padding: 1rem 1.25rem;
}

.filter-label {
  display: block;
  font-size: 0.75rem;
  font-weight: 600;
  color: #6c757d;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 0.35rem;
}

.filter-panel .form-control,
.filter-panel .form-select {
  height: 34px;
  font-size: 0.85rem;
  border-color: #dee2e6;
}

.filter-panel .form-control:focus,
.filter-panel .form-select:focus {
  border-color: #556ee6;
  box-shadow: 0 0 0 0.15rem rgba(85, 110, 230, 0.15);
}

/* Severity Checkboxes */
.severity-filters {
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
  padding-top: 2px;
}

.severity-checkbox {
  display: inline-flex;
  align-items: center;
  padding: 0.35rem 0.75rem;
  border-radius: 4px;
  font-size: 0.8rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s ease;
  border: 1px solid transparent;
  background: #f8f9fa;
}

.severity-checkbox input[type="checkbox"] {
  display: none;
}

.severity-checkbox.is-checked {
  border-color: currentColor;
  background: rgba(0, 0, 0, 0.05);
}

.severity-checkbox .checkmark {
  width: 14px;
  height: 14px;
  border: 2px solid #adb5bd;
  border-radius: 3px;
  margin-right: 6px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: all 0.15s ease;
}

.severity-checkbox input[type="checkbox"]:checked + .checkmark {
  background: currentColor;
  border-color: currentColor;
}

.severity-checkbox input[type="checkbox"]:checked + .checkmark::after {
  content: "";
  width: 4px;
  height: 7px;
  border: solid #fff;
  border-width: 0 2px 2px 0;
  transform: rotate(45deg);
  margin-bottom: 2px;
}

/* Severity Colors */
.severity-critical {
  color: #802020;
}
.severity-critical:hover {
  background: rgba(128, 32, 32, 0.1);
}
.severity-critical.is-checked {
  background: rgba(128, 32, 32, 0.15);
  border-color: #802020;
}

.severity-high {
  color: #DC001E;
}
.severity-high:hover {
  background: rgba(220, 0, 30, 0.1);
}
.severity-high.is-checked {
  background: rgba(220, 0, 30, 0.15);
  border-color: #DC001E;
}

.severity-medium {
  color: #FECA00;
}
.severity-medium:hover {
  background: rgba(254, 202, 0, 0.15);
}
.severity-medium.is-checked {
  background: rgba(254, 202, 0, 0.2);
  border-color: #FECA00;
}

.severity-low {
  color: #01AD00;
}
.severity-low:hover {
  background: rgba(1, 173, 0, 0.1);
}
.severity-low.is-checked {
  background: rgba(1, 173, 0, 0.15);
  border-color: #01AD00;
}

/* Updated text */
.updated-text {
  font-size: 0.8rem;
  color: #6c757d;
  display: inline-flex;
  align-items: center;
  padding-top: 1.5rem;
}

/* Stats Cards */
.mini-stats-wid .card-body {
  padding: 1rem;
}

.mini-stat-icon {
  width: 48px;
  height: 48px;
}

.avatar-title {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  color: #fff;
}

.avatar-title .icon-sm {
  width: 20px;
  height: 20px;
}

/* Table Styles */
.sortable {
  cursor: pointer;
}

.sortable:hover {
  background-color: rgba(0, 0, 0, 0.05);
}

.sortable.active {
  background-color: rgba(0, 0, 0, 0.075);
}

.table th {
  font-size: 0.8rem;
  white-space: nowrap;
}

.table td {
  font-size: 0.8rem;
  vertical-align: middle;
}

/* Column widths */
.col-cve {
  min-width: 120px;
  max-width: 140px;
}

.col-asset {
  min-width: 120px;
  max-width: 160px;
}

.col-asset span,
.col-owner span,
.col-package span {
  display: inline-block;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  cursor: help;
}

.col-type {
  min-width: 80px;
  max-width: 100px;
}

.col-owner {
  min-width: 100px;
  max-width: 120px;
}

.col-severity {
  min-width: 70px;
  max-width: 80px;
}

.col-cvss {
  min-width: 50px;
  max-width: 60px;
  text-align: center;
}

.col-package {
  min-width: 100px;
  max-width: 120px;
}

.col-flag {
  min-width: 50px;
  max-width: 60px;
  text-align: center;
}

.col-vector {
  min-width: 70px;
  max-width: 80px;
}

.col-date {
  min-width: 90px;
  max-width: 100px;
}

.badge {
  font-size: 0.75rem;
}

.card-title {
  font-size: 1rem;
  font-weight: 600;
}

/* Severity Legend */
.severity-legend {
  display: flex;
  gap: 0.75rem;
  align-items: center;
}

.legend-item {
  display: inline-flex;
  align-items: center;
  font-size: 0.75rem;
  color: #6c757d;
}

.legend-color {
  display: inline-block;
  width: 12px;
  height: 12px;
  border-radius: 2px;
  margin-right: 4px;
}

.bg-severity-critical {
  background-color: #802020;
}

.bg-severity-high {
  background-color: #DC001E;
}

.bg-severity-medium {
  background-color: #FECA00;
}

.bg-severity-low {
  background-color: #01AD00;
}

/* Badge severity colors for table */
.badge-severity-critical {
  background-color: #802020;
  color: #fff;
}

.badge-severity-high {
  background-color: #DC001E;
  color: #fff;
}

.badge-severity-medium {
  background-color: #FECA00;
  color: #212529;
}

.badge-severity-low {
  background-color: #01AD00;
  color: #fff;
}

/* Yes/No badge colors */
.badge-yes {
  background-color: #E57373;
  color: #fff;
}

.badge-no {
  background-color: #81C784;
  color: #fff;
}

/* Table Loading Overlay */
.table-loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.85);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 10;
  border-radius: 0.25rem;
}

.table-loading-overlay .spinner-border {
  width: 2.5rem;
  height: 2.5rem;
}

/* Responsive adjustments */
@media (max-width: 991px) {
  .severity-filters {
    margin-top: 0.5rem;
  }

  .updated-text {
    padding-top: 0.5rem;
    justify-content: flex-start;
  }
}
</style>
